import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { Address, MapboxFeature } from '../../types/Address'
import { WaterSystemsActions, WaterSystemsSelector, WaterSystemType } from '../../store/waterSystems'
import { WaterSystemCoordinatesActions, WaterSystemsMetricsActions } from '../../store/publicWaterSystem'
import { AmericanStatesSelector } from '../../store/AmericanStates'
import { AddressActions, AddressSelector } from '../../store/Address'
import { handleMapZoomOnZip } from '../../utils/mapHelper'
import { AppActions } from '../../store/app'
import { UtilityDetailActions } from '../../store/utilityDetails'
import ReactPixel from 'react-facebook-pixel'
import ReactGA from 'react-ga4'

export const useAddressSearch = () => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<MapboxFeature[]>([])
  const [error, setError] = useState('')
  const [debounce, setDebounce] = useState<any>()
  const [address, setAddress] = useState<Address>({
    streetAndNumber: '',
    place: '',
    region: '',
    postcode: '',
    country: '',
    latitude: 0,
    longitude: 0
  })

  const americanStates = useSelector(AmericanStatesSelector.getStates)
  const selectedAddress = useSelector(AddressSelector.selectAddress)
  const waterSystemType = useSelector(WaterSystemsSelector.getSelectedWaterSystemType)
  useEffect(() => {

    if(!selectedAddress){
      setAddress({
        streetAndNumber: '',
        place: '',
        region: '',
        postcode: '',
        country: '',
        latitude: 0,
        longitude: 0
      })
    }
  }, [selectedAddress])

  const resetSearchedData = () => {
    dispatch(WaterSystemsActions.setSelectedWaterSystem(''))
    dispatch(WaterSystemsActions.setUserSearched(false))
    dispatch(WaterSystemsActions.resetWaterSystemsData())
    dispatch(WaterSystemsMetricsActions.resetWaterSystemMetricsData())
    dispatch(WaterSystemCoordinatesActions.resetWaterSystemCoordinates())
  }
  const fetchMapboxPlaces = async (query) => {
    const mapBoxAccessToken =
      (window.__ENV__ &&
        window.__ENV__?.MAPBOX_ACCESS_KEY !== '__MAPBOX_ACCESS_KEY__' &&
        window.__ENV__?.MAPBOX_ACCESS_KEY) ||
      process.env.REACT_APP_MAPBOX_ACCESS_KEY ||
      ''

    return await axios.get(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`,
      {
        params: {
          access_token:mapBoxAccessToken,
          country: 'US',
          types: 'address,place,locality'
        },
      }
    )
  }

  const handleManualInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    clearTimeout(debounce)
    const query = event.target.value
    setError('')
    
    setAddress(prev => ({
      ...prev,
      streetAndNumber: query
    }))

    if (!query || query.length < 3) {
      setSuggestions([])
      dispatch(AddressActions.setSelectedAddress(null))
      return
    }

    const t = setTimeout(async () => {
      try {
        setIsLoading(true)
        const response = await fetchMapboxPlaces(query)


        if (response.data.features.length === 0) {
          setError('No matching addresses found')
        }

        setSuggestions(response.data.features)
      } catch (error) {
        console.error('Error fetching suggestions:', error)
        setSuggestions([])
        setError('Unable to search for addresses')
      } finally {
        setIsLoading(false)
      }
      },800)
    setDebounce(t)
  }
  const handleSubmit = async () => {
    if(!address || address.streetAndNumber.length < 3 ) {
      setSuggestions([])
      dispatch(AddressActions.setSelectedAddress(null))
      return;
    }
    try {
      setIsLoading(true)
      const response = await fetchMapboxPlaces(address.streetAndNumber)
      if (response.data.features.length === 0) {
        setError('No matching addresses found')
      }

      await handleSuggestionClick(response.data.features[0])()
    } catch(error) {
      console.error('Error fetching suggestions:', error)
      setSuggestions([])
      setError('Unable to search for addresses')
    } finally {
      setIsLoading(false)
    }

  }
  const handleSuggestionClick = (suggestion: MapboxFeature) => async () => {

    const addressParts = suggestion.place_name.split(',')
    const streetAndNumber = addressParts.slice(0, 3).join(',')
    const [longitude, latitude] = suggestion.center

    const newAddress = {
      streetAndNumber,
      place: '',
      region: '',
      postcode: '',
      country: '',
      latitude,
      longitude
    }

    suggestion.context?.forEach((element) => {
      const identifier = element.id.split('.')[0]
      switch (identifier) {
        case 'place':
          newAddress.place = element.text
          break
        case 'region':
          newAddress.region = element.text
          break
        case 'postcode':
          newAddress.postcode = element.text
          break
        case 'country':
          newAddress.country = element.text
          break
      }
    })

    setAddress(newAddress)
    setSuggestions([])

    try {

      setIsLoading(true)
      setError('')
      
      resetSearchedData()

      if( waterSystemType !== 'well'){
        dispatch(AppActions.setMapDataDrawerOpen(true))
        dispatch(UtilityDetailActions.closeDrawer());
      }
      ReactPixel.trackCustom("click", {id:'address_search_submit',address: newAddress});
      ReactGA.event({action: 'click', category:'User event tracking', label:'Form - address search'})
      const resultAction = await dispatch(
        WaterSystemsActions.fetchPublicWaterSystemsByAddressAsync({
          latitude,
          longitude,
        }) as any,
      )

      if (WaterSystemsActions.fetchPublicWaterSystemsByAddressAsync.fulfilled.match(resultAction)) {
        handleMapZoomOnZip(resultAction.payload, americanStates, dispatch)
      }

      dispatch(AddressActions.setSelectedAddress(newAddress))
      dispatch(WaterSystemsActions.setUserSearched(true))

      
      if (window.gtag) {
        window.gtag('event', 'search', {
          event_category: 'Search',
          event_label: 'Address Search',
          value: newAddress,
        })
      }

    } catch (error) {
      console.error('Error updating map and fetching water systems:', error)
      setError('Unable to load water systems for this address')
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    error,
    address,
    suggestions,
    setAddress,
    handleManualInputChange,
    handleSuggestionClick,
    handleSubmit,
  }
}